<div class="flex flex-col gap-y-10">
  <p class="text-sm text-gray">
    Remaining positions below form the Custom Holdings sleeve. Check any that should <strong><em>NOT</em></strong>
    be traded.
  </p>

  @if (store.resultingHoldings().length === 0) {
    <p class="p-20 text-sm italic text-center text-gray">No remaining positions.</p>
  } @else {
    <div class="w-full overflow-x-auto">
      <table class="w-full text-sm border-collapse table-fixed">
        <colgroup>
          <col style="width: 12%" />
          <col style="width: 16%" />
          <col style="width: 27%" />
          <col style="width: 11%" />
          <col style="width: 11%" />
          <col style="width: 12%" />
          <col style="width: 11%" />
        </colgroup>
        <thead>
          <tr class="text-gray-dark">
            <th class="p-8 text-left border-b border-gray-lighter">
              <div class="flex items-center gap-x-5">
                <input type="checkbox" class="accent-mint-darkest" aria-label="Mark all positions Do Not Trade"
                  [checked]="store.dntAllState().checked"
                  [indeterminate]="store.dntAllState().indeterminate"
                  (change)="onDntToggleAll($event)" />
                <span>Do Not Trade</span>
              </div>
            </th>
            <th class="p-8 text-left border-b border-gray-lighter">Ticker</th>
            <th class="p-8 text-left border-b border-gray-lighter">Description</th>
            <th class="p-8 text-right border-b border-gray-lighter">Shares</th>
            <th class="p-8 text-right border-b border-gray-lighter">Price</th>
            <th class="p-8 text-right border-b border-gray-lighter">Est. Market Value</th>
            <th class="p-8 text-right border-b border-gray-lighter">Est. % of Acct</th>
          </tr>
        </thead>
        <tbody>
          <!-- The MERGED list: kept positions and buys of the same security are one row,
               so an advisor who keeps AAPL and buys more marks it once rather than twice. -->
          @for (p of store.resultingHoldings(); track p.ticker) {
            <tr class="border-b border-gray-lightest" [class.bg-orange-lightest]="p.isDoNotTrade">
              <td class="p-8">
                <div class="flex items-center gap-x-8">
                  <input type="checkbox" class="accent-mint-darkest" [checked]="p.isDoNotTrade" [disabled]="!p.dntEligible"
                    (change)="onDntToggle(p.ticker, $event)" [attr.aria-label]="'Do Not Trade ' + p.ticker" />
                  @if (p.isDoNotTrade) {
                    <span class="px-8 py-1 text-orange-dark bg-orange-lightest border border-orange-lighter rounded-full"
                      style="font-size: 1rem">DNT</span>
                  }
                </div>
              </td>
              <td class="p-8">
                <div class="flex flex-wrap items-center gap-x-5 gap-y-2">
                  <span class="font-semibold text-gray-darkest">{{ p.ticker }}</span>
                  @if (p.isNewPosition) {
                    <span class="px-8 py-1 text-mint-darkest border rounded-full bg-mint-lighter border-mint-light"
                      style="font-size: 1rem">New — buying {{ p.boughtShares | number }}</span>
                  } @else if (p.boughtShares > 0) {
                    <span class="px-8 py-1 text-mint-darkest border rounded-full bg-mint-lighter border-mint-light"
                      style="font-size: 1rem">Keeping + buying {{ p.boughtShares | number }}</span>
                  }
                </div>
              </td>
              <td class="p-8">{{ p.description }}</td>
              <td class="p-8 text-right">{{ p.shares != null ? (p.shares | number) : '—' }}</td>
              <td class="p-8 text-right">
                {{ p.shares ? (p.value / p.shares | currency) : '—' }}
              </td>
              <td class="p-8 text-right">
                <!-- No per-row estimate marker. It was a leading tilde, and pilot users read it
                     as a minus sign -- in a right-aligned currency column that is a costly
                     misread, since the figure it decorated is a positive holding value.
                     
                     The header carries the caveat for the whole column instead, which is also
                     the truer claim: every figure here is priced at load time, so the rows a buy
                     did not touch are no more reconciled than the ones it did -- they will have
                     moved by the time the trade team acts. ResultingHolding.isEstimated still
                     marks the narrower case for anything that needs it. -->
                {{ p.value | currency }}
              </td>
              <td class="p-8 text-right">
                {{ p.pctOfAccount | number: '1.2-2' }}%
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  }

  <p class="text-sm text-gray">
    {{ dntCount }} position{{ dntCount === 1 ? '' : 's' }} marked Do Not Trade
  </p>

  <!--
    The attestation. Appears only once something is marked, because with nothing marked
    there is nothing to attest to and the step is one the advisor passes straight through.

    The marked tickers are listed inside the panel rather than left to be re-read off the
    table above. What is being confirmed is that the list is *complete and correct*, and a
    checkbox next to an abstract claim collects a tick, not a check. Seeing the names is
    the difference between attesting and clicking.

    Orange, matching the DNT row highlight and the badge, so the panel is visibly about
    the same thing the table is showing. Red would read as an error; nothing is wrong.
  -->
  @if (store.markedDoNotTrade().length) {
    <div class="flex flex-col px-15 py-10 border rounded gap-y-8 border-orange-lighter bg-orange-lightest">
      <div class="flex items-start text-sm gap-x-8 text-orange-darker">
        <i class="material-icons" style="font-size: 1.6rem" aria-hidden="true">error_outline</i>
        <span>Please confirm you have selected all the positions you do <strong>NOT</strong> want traded.</span>
      </div>

      <div class="text-sm text-gray-darkest">
        <span class="font-semibold">Marked Do Not Trade:</span>
        {{ markedTickers }}
      </div>

      <!--
        A <label> wrapping both, so the text is part of the hit area — an attestation
        should not be fiddly to give, only deliberate to give.
      -->
      <label class="flex items-center text-sm cursor-pointer gap-x-8 text-gray-darkest">
        <input type="checkbox" class="accent-mint-darkest" [checked]="store.dntConfirmed()" (change)="onConfirm($event)" />
        <span>I have verified these selections</span>
      </label>
    </div>
  }
</div>
